import React, { useState, useEffect, useRef } from 'react';
import { SearchResultChalet } from './SearchResultChalet';
import ChaletSearchResultRefiners from './ChaletSearchResultRefiners';
import MapSection from './refiners/MapSection';
import ChaletSearchResultsIntroText from './ChaletSearchResultsIntroText'

const ChaletSearchResultsInspiration = ({ updateSearchCount, inspireMeAttributeId }) => {
    const [searchAlreadyRun, setSearchAlreadyCalled] = useState(false);
    const [allChalets, setAllChalets] = useState([]);
    const [displayChalets, setDisplayChalets] = useState([]); // Chalets to display
    const chaletListRef = useRef(null);
    const [shouldScroll, setShouldScroll] = useState(false);
    const [searchQuery, setSearchQuery] = useState(null);

    const [returnedChalets, setReturnedChalets] = useState(0);

    const [filterCriteria, setFilterCriteria] = useState({
        minPrice: 0,
        maxPrice: Infinity,
        minResortDistance: 0,
        maxResortDistance: Infinity,
        maxResortDistance: Infinity,
        minLiftDistance: 0,
        maxLiftDistance: Infinity,
        minReviewScore: 0,
        maxReviewScore: 10,
        selectedResorts: [],
        selectedInspireMeAttributes: [],
        selectedAccommodationTypes: []
    });

    const apiUrl = process.env.REACT_APP_API_URL;
    const hasRun = useRef(false); // Ref to keep track if the effect has run

    useEffect(() => {
        if (inspireMeAttributeId && inspireMeAttributeId !== 0 && !hasRun.current) {
            executeSearch(inspireMeAttributeId);
            hasRun.current = true; // Set the ref to true after running
        }
    }, [inspireMeAttributeId]);


    useEffect(() => {
        if (shouldScroll && chaletListRef.current) {
            chaletListRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
            setShouldScroll(false); // Reset the flag to avoid unnecessary future scrolls
        }
    }, [shouldScroll]);




    const executeSearch = (id) => {

        if (id === 0) {
            return;
        }

        if (searchAlreadyRun) {
            return;
        }

        setSearchAlreadyCalled(true);

        if (allChalets.length > 0) {
            return;
        }

        try {

            fetch(`${apiUrl}/SearchByInspireMeAttribute/${id}`)
                .then(response => response.json())
                .then(data => {
                    if (data.properties && data.properties.length > 0) {
                        const fetchedChalets = data.properties.map(chalet => ({
                            id: chalet.chaletId,
                            name: chalet.name,
                            image: chalet.thumbnailImageUrl,
                            price: chalet.totalQueryCost,
                            total: chalet.totalQueryCost,
                            currencySymbol: chalet.currencySymbol,
                            country: chalet.countryName,
                            resort: chalet.resort.name,
                            resortID: chalet.resort.id,
                            resortLat: chalet.resort.lat,
                            resortLng: chalet.resort.lng,
                            link: chalet.internalUrl,
                            distanceFromResortCentreMeters: chalet.distanceFromResortCentreMeters,
                            distanceFromLiftMeters: chalet.distanceFromLiftMeters,
                            reviewScore: chalet.reviewScore,
                            lat: chalet.lat,
                            lng: chalet.lng,
                            accommodationTypeName: chalet.accommodationTypeName,
                            resortInspireMeAttributes: data.resortInspireMeAttributes.filter(attr => attr.resort.id === chalet.resort.id),
                            totalStayFormatted: `${chalet.currencySymbol}${chalet.totalQueryCost.toLocaleString('en-GB', {
                                style: 'decimal',
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0
                            })} for total stay`
                        }));

                        setSearchQuery(data.searchQuery);

                        setResortList(prevResorts => {
                            const resortUpdates = fetchedChalets.map(({ resort, resortID, resortTitle }) => ({
                                name: resort,
                                id: resortID,
                                title: resortTitle
                            }));

                            const newResortList = [...prevResorts];
                            resortUpdates.forEach(resort => {
                                if (!newResortList.some(r => r.id === resort.id)) {
                                    newResortList.push(resort);
                                }
                            });

                            return newResortList;
                        });

                        setInspireMeAttributeList(prevAttributes => {
                            const inspireMeAttributeUpdates = data.resortInspireMeAttributes.map(attribute => ({
                                resortId: attribute.resort.id,
                                resortName: attribute.resort.name,
                                attributeId: attribute.inspireMeAttribute.id,
                                attributeName: attribute.inspireMeAttribute.attributeName,
                                description: attribute.inspireMeAttribute.description
                            }));

                            const attributeIdSet = new Set(prevAttributes.map(attr => attr.attributeId));
                            const newAttributes = inspireMeAttributeUpdates.filter(attribute => {
                                if (!attributeIdSet.has(attribute.attributeId)) {
                                    attributeIdSet.add(attribute.attributeId);
                                    return true;
                                }
                                return false;
                            });

                            return [...prevAttributes, ...newAttributes];
                        });

                        // Extract and update accommodation types list without duplicates
                        setAccommodationTypeList(prevTypes => {
                            // Map the accommodationTypeNameList to create an array of objects with accommodationTypeName property
                            const accommodationTypeUpdates = data.accommodationTypeNameList.map(typeName => ({
                                accommodationTypeName: typeName
                            }));

                            // Create a set to track unique accommodation type names from the previous list
                            const typeNameSet = new Set(prevTypes.map(type => type.accommodationTypeName));

                            // Filter the updates to only include new types not already in the set
                            const newTypes = accommodationTypeUpdates.filter(type => {
                                if (!typeNameSet.has(type.accommodationTypeName)) {
                                    typeNameSet.add(type.accommodationTypeName);
                                    return true;
                                }
                                return false;
                            });

                            // Return the combined list of previous and new types
                            return [...prevTypes, ...newTypes];
                        });



                        setPriceRangeMarks(priceRangeMarks => {
                            const combinedMarks = [...priceRangeMarks, ...data.priceRefinerMarks];
                            const sortedMarks = combinedMarks.sort((a, b) => a.value - b.value);
                            const uniqueSortedMarks = sortedMarks.filter((mark, index, self) =>
                                index === self.findIndex(t => t.value === mark.value)
                            );

                            const lowestMark = uniqueSortedMarks[0];
                            const highestMark = uniqueSortedMarks.at(-1);
                            const middleValue = (lowestMark.value + highestMark.value) / 2;
                            const middleMark = uniqueSortedMarks.reduce((prev, curr) =>
                                Math.abs(curr.value - middleValue) < Math.abs(prev.value - middleValue) ? curr : prev
                            );

                            return [lowestMark, middleMark, highestMark];
                        });

                        setResortCentreRangeMarks(resortCentreRangeMarks => {
                            const combinedMarks = [...resortCentreRangeMarks, ...data.distancetoCentreRefinerMarks];
                            const sortedMarks = combinedMarks.sort((a, b) => a.value - b.value);
                            const uniqueSortedMarks = sortedMarks.filter((mark, index, self) =>
                                index === self.findIndex(t => t.value === mark.value)
                            );

                            const lowestMark = uniqueSortedMarks[0];
                            const highestMark = uniqueSortedMarks.at(-1);
                            const middleMark = { value: (lowestMark.value + highestMark.value) / 2, label: `${(lowestMark.value + highestMark.value) / 2}m` };

                            return [lowestMark, { value: middleMark.value, label: middleMark.label }, highestMark];
                        });

                        setSkiLitRangeMarks(skiLiftRangeMarks => {
                            const combinedMarks = [...skiLiftRangeMarks, ...data.distancetoSkiLiftRefinerMarks];
                            const sortedMarks = combinedMarks.sort((a, b) => a.value - b.value);
                            const uniqueSortedMarks = sortedMarks.filter((mark, index, self) =>
                                index === self.findIndex(t => t.value === mark.value)
                            );

                            const lowestMark = uniqueSortedMarks[0];
                            const highestMark = uniqueSortedMarks.at(-1);
                            const middleMark = { value: (lowestMark.value + highestMark.value) / 2, label: `${(lowestMark.value + highestMark.value) / 2}m` };

                            return [lowestMark, { value: middleMark.value, label: middleMark.label }, highestMark];
                        });

                        loadChaletSettings(fetchedChalets);

                        setShouldScroll(true);

                    } else {
                    }
                })
                .catch(error => {
                    //console.error("There was a problem with the fetch operation: ", error);
                });
        } catch (error) {
            //.error("Error in executeSearch:", error);
        }
    };


    useEffect(() => {
        const filteredChalets = allChalets.filter(chalet => {
            const resortAttributes = chalet.resortInspireMeAttributes || [];
            const hasSelectedAttributes = filterCriteria.selectedInspireMeAttributes.every(attr =>
                resortAttributes.some(resortAttr => resortAttr.inspireMeAttribute.id === attr)
            );
            // Check if the chalet's accommodation type matches one of the selected types
            const hasSelectedAccommodationType = filterCriteria.selectedAccommodationTypes.length === 0 ||
                filterCriteria.selectedAccommodationTypes.includes(chalet.accommodationTypeName);

            return Number(chalet.total) >= filterCriteria.minPrice &&
                Number(chalet.total) <= filterCriteria.maxPrice &&
                Number(chalet.distanceFromResortCentreMeters) >= filterCriteria.minResortDistance &&
                Number(chalet.distanceFromResortCentreMeters) <= filterCriteria.maxResortDistance &&
                Number(chalet.distanceFromLiftMeters) >= filterCriteria.minLiftDistance &&
                Number(chalet.distanceFromLiftMeters) <= filterCriteria.maxLiftDistance &&
                Number(chalet.reviewScore) >= filterCriteria.minReviewScore &&
                Number(chalet.reviewScore) <= filterCriteria.maxReviewScore &&
                (filterCriteria.selectedResorts.length === 0 || filterCriteria.selectedResorts.includes(chalet.resortID)) &&
                (filterCriteria.selectedInspireMeAttributes.length === 0 || hasSelectedAttributes) &&
                hasSelectedAccommodationType; // Ensure it matches one of the selected types
        });
        setDisplayChalets(filteredChalets);
    }, [allChalets, filterCriteria]);

    const updateFilterCriteria = (criteria) => {
        setFilterCriteria(prev => ({ ...prev, ...criteria }));
    };

    const [resortList, setResortList] = useState([]);
    const onResortCheckBoxChange = (selectedResortIds) => {
        const selectedResortIdsInt = selectedResortIds.map(id => parseInt(id, 10));
        updateFilterCriteria({ selectedResorts: selectedResortIdsInt });
    };

    const resortCheckListSettings = {
        title: "Resorts",
        onSelectionChange: onResortCheckBoxChange,
        items: resortList
    };

    const [inspireMeAttributeList, setInspireMeAttributeList] = useState([]);
    const onInspireMeAttributeCheckBoxChange = (selectedInspireMeAttributeIds) => {
        const selectedInspireMeAttributeIdsInt = selectedInspireMeAttributeIds.map(id => parseInt(id, 10));
        updateFilterCriteria({ selectedInspireMeAttributes: selectedInspireMeAttributeIdsInt });
    };

    const inspireMeAttributeCheckboxListSettings = {
        title: "Inspire Me Categories",
        onSelectionChange: onInspireMeAttributeCheckBoxChange,
        items: inspireMeAttributeList
    };

    // Accommodation Type Checkbox List Code
    const [accommodationTypeList, setAccommodationTypeList] = useState([]);
    const onAccommodationTypeCheckBoxChange = (selectedAccommodationTypeNames) => {
        updateFilterCriteria({ selectedAccommodationTypes: selectedAccommodationTypeNames });
    };


    const accommodationTypeCheckboxListSettings = {
        title: "Accommodation Type",
        onSelectionChange: onAccommodationTypeCheckBoxChange,
        items: accommodationTypeList
    };




    const [defaultPriceRange, setDefaultPriceRange] = useState(null);
    const [priceRange, setPriceRange] = useState([0, 1000]); // Default price range
    const [priceRangeMarks, setPriceRangeMarks] = useState([]);
    const onPriceSliderChange = (newValue) => {
        const [minPrice, maxPrice] = newValue;
        updateFilterCriteria({ minPrice, maxPrice });
    };

    const priceSliderSettings = {
        title: "Total Price",
        min: priceRange[0],
        max: priceRange[1],
        step: 50,
        defaultValue: defaultPriceRange,
        onChange: onPriceSliderChange,
        marks: priceRangeMarks
    };

    const [defaultResortCentreRange, setdefaultResortCentreRange] = useState(null);
    const [ResortCentreRange, setResortCentreRange] = useState([0, 5000]); // Default price range
    const [ResortCentreRangeMarks, setResortCentreRangeMarks] = useState([]);
    const onResortDistanceSliderChange = (newValue) => {
        const [minResortDistance, maxResortDistance] = newValue;
        updateFilterCriteria({ minResortDistance, maxResortDistance });
    };

    const distanceResortCentreSliderSettings = {
        title: "Distance to Resort Centre",
        min: ResortCentreRange[0],
        max: ResortCentreRange[1],
        step: 50,
        defaultValue: defaultResortCentreRange,
        onChange: onResortDistanceSliderChange,
        marks: ResortCentreRangeMarks
    };

    const [defaultSkiLiftRange, setdefaultSkiLiftRange] = useState(null);
    const [SkiLiftRange, setSkiLiftRange] = useState([0, 5000]); // Default price range
    const [SkiLitRangeMarks, setSkiLitRangeMarks] = useState([]);
    const onLiftDistanceSliderChange = (newValue) => {
        const [minLiftDistance, maxLiftDistance] = newValue;
        updateFilterCriteria({ minLiftDistance, maxLiftDistance });
    };

    const distanceSkiLiftliderSettings = {
        title: "Distance to Closest Ski Lift",
        min: SkiLiftRange[0],
        max: SkiLiftRange[1],
        step: 50,
        defaultValue: defaultSkiLiftRange,
        onChange: onLiftDistanceSliderChange,
        marks: SkiLitRangeMarks
    };

    const onRatingCompoentChange = (newValue) => {
        updateFilterCriteria({ minReviewScore: 0, maxReviewScore: newValue });
    }

    const propertyRatingSettings = {
        name: "Chalet Ratings",
        defaultValue: 10,
        onChange: onRatingCompoentChange,
    };

    const loadChaletSettings = (fetchedChalets) => {
        setAllChalets(prevChalets => {
            const updatedChalets = [...prevChalets, ...fetchedChalets];
            const lowestPrice = updatedChalets.reduce((min, chalet) => Math.min(min, chalet.total), Infinity);
            const highestPrice = updatedChalets.reduce((max, chalet) => Math.max(max, chalet.total), 0);

            const lowestDistanceToResortCentre = updatedChalets.reduce((min, chalet) => Math.min(min, chalet.distanceFromResortCentreMeters), Infinity);
            const highestDistanceToResortCentre = updatedChalets.reduce((max, chalet) => Math.max(max, chalet.distanceFromResortCentreMeters), 0);

            const lowestDistanceToSkiLift = updatedChalets.reduce((min, chalet) => Math.min(min, chalet.distanceFromLiftMeters), Infinity);
            const highestDistanceToSkiLift = updatedChalets.reduce((max, chalet) => Math.max(max, chalet.distanceFromLiftMeters), 0);

            setPriceRange([lowestPrice, highestPrice]);
            setDefaultPriceRange([lowestPrice, highestPrice]);

            setResortCentreRange([lowestDistanceToResortCentre, highestDistanceToResortCentre]);
            setdefaultResortCentreRange([lowestDistanceToResortCentre, highestDistanceToResortCentre]);

            setdefaultSkiLiftRange([lowestDistanceToSkiLift, highestDistanceToSkiLift]);
            setSkiLiftRange([lowestDistanceToSkiLift, highestDistanceToSkiLift]);

            return updatedChalets;
        });

        setDisplayChalets(prevDisplayChalets => [...prevDisplayChalets, ...fetchedChalets]);
        updateSearchCount(fetchedChalets.length);
        setReturnedChalets(fetchedChalets.length);
    };

    return (
        <div ref={chaletListRef}>
            {
          
            }
            <section>
                <div className="row">
                    <div className="col-12 col-lg-3 col-xl-2">
           
                    </div>
                    <div className="col-12 col-lg-9 col-xl-10">
                        <div className="bg-light">
                            <ChaletSearchResultsIntroText inspireMeAttributeId={inspireMeAttributeId} />
                        </div>
                    </div>
                </div>


                <div className="row">
                    <div className="col-12 col-lg-9 col-xl-10 offset-lg-3 offset-xl-2 p-4">
                        <MapSection displayChalets={displayChalets} zoom={7} />
                    </div>
                </div>
                <div className="row">
                    <div className="col-12 col-lg-3 col-xl-2">
                        <div className="sidebar-box">
                            <ChaletSearchResultRefiners
                                priceSliderSettings={priceSliderSettings}
                                distanceResortCentreSliderSettings={distanceResortCentreSliderSettings}
                                distanceSkiLiftliderSettings={distanceSkiLiftliderSettings}
                                propertyRatingSettings={propertyRatingSettings}
                                resortCheckListSettings={resortCheckListSettings}
                                inspireMeAttributeCheckboxListSettings={inspireMeAttributeCheckboxListSettings}
                                accommodationTypeCheckboxListSettings={accommodationTypeCheckboxListSettings}
                            />
                        </div>
                    </div>
                    {
                        displayChalets.length > 0 ?
                            (
                                (() => {
                                    return (
                                        <div className="col-12 col-lg-9 col-xl-10">
                                            <div className="row">
                                                {displayChalets.map(item => <SearchResultChalet item={item} key={item.id} />)}
                                            </div>
                                        </div>
                                    );
                                })()
                            ) :
                            (
                                <div className="col-12 col-lg-8 col-xl-8">
                                    <div className="row">
                                        <h3>Results</h3>
                                        <p>Sadly, no Chalets available</p>
                                    </div>
                                </div>
                            )
                    }
                </div>
            </section>
        </div>
    );





};

export default ChaletSearchResultsInspiration;
